import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import Select from "react-select";
import { TextField } from "@material-ui/core";
import { Alert, Snackbar } from "@mui/material";
import ProductPurchaseService from "../../services/ProductPurchaseService";
import WarehouseService from "../../services/WarehouseService";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import withLoader from "../../components/layouts/WithLoader";

const DEFAULT_PAGE_SIZE = 20;
const ALL_WAREHOUSES = { value: "", label: "Tous" };

const emptyFilters = {
  dateFrom: "",
  dateTo: "",
  idWarehouse: "",
  brandName: "",
  productCode: "",
};

const PrixAchat = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalSize, setTotalSize] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [allWarehouses, setAllWarehouses] = useState([ALL_WAREHOUSES]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(ALL_WAREHOUSES);
  // filters being edited in the form
  const [filters, setFilters] = useState(emptyFilters);
  // filters actually applied to the API call
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorAlertText, setErrorAlertText] = useState();
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("status") === null) {
      setUserRole(["notConnected"]);
    } else {
      setUserRole(JSON.parse(localStorage.getItem("status")));
    }
  }, []);

  useEffect(() => {
    if (!userRole.includes("ADMIN")) {
      return;
    }
    WarehouseService.getAllRealWarehouse(getCurrentUserId(), 0)
      .then((response) => {
        if (response.data.success) {
          setAllWarehouses([
            ALL_WAREHOUSES,
            ...response.data.success.map((element) => ({
              value: element.id,
              label: element.name,
            })),
          ]);
        }
      })
      .catch((e) => {
        console.log("error in getAllRealWarehouse", e);
      });
  }, [userRole]);

  const fetchPurchases = (page, size, activeFilters) => {
    setIsLoading(true);
    startLoading();
    ProductPurchaseService.getProductPurchasesSummary(getCurrentUserId(), {
      page: page - 1, // API uses 0-based page indexing
      size: size,
      ...activeFilters,
    })
      .then((response) => {
        if (response.data.success) {
          const data = response.data.success;
          setPurchases(
            data.content.map((element, i) => ({
              ...element,
              rowKey: `${element.productCode}-${element.idWarehouse}-${i}`,
            }))
          );
          setTotalSize(data.totalElements);
        } else {
          setPurchases([]);
          setTotalSize(0);
          if (response.data.error) {
            setOpenErrorAlert(true);
            setErrorAlertText("Erreur : " + response.data.error);
          }
        }
      })
      .catch((e) => {
        setOpenErrorAlert(true);
        setErrorAlertText("Erreur : " + e);
      })
      .finally(() => {
        setIsLoading(false);
        stopLoading();
      });
  };

  useEffect(() => {
    if (userRole.includes("ADMIN")) {
      fetchPurchases(1, DEFAULT_PAGE_SIZE, emptyFilters);
    }
  }, [userRole]);

  const onChangeDateFrom = (event) => {
    const value = event.target.value;
    setFilters({ ...filters, dateFrom: value ? value + "T00:00:00" : "" });
  };

  const onChangeDateTo = (event) => {
    const value = event.target.value;
    setFilters({ ...filters, dateTo: value ? value + "T23:59:59" : "" });
  };

  const onChangeWarehouse = (option) => {
    setSelectedWarehouse(option);
    setFilters({ ...filters, idWarehouse: option.value });
  };

  const onApplyFiltersHandler = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    fetchPurchases(1, sizePerPage, filters);
  };

  const onResetFiltersHandler = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSelectedWarehouse(ALL_WAREHOUSES);
    setCurrentPage(1);
    fetchPurchases(1, sizePerPage, emptyFilters);
  };

  // Exports every row matching the applied filters, not just the current page
  const onExportCSVHandler = async () => {
    setExporting(true);
    try {
      const exportPageSize = 1000;
      let rows = [];
      let page = 0;
      let totalPages = 1;
      while (page < totalPages) {
        const response = await ProductPurchaseService.getProductPurchasesSummary(
          getCurrentUserId(),
          { page: page, size: exportPageSize, ...appliedFilters }
        );
        if (!response.data.success) {
          throw new Error(response.data.error || "export impossible");
        }
        rows = rows.concat(response.data.success.content);
        totalPages = response.data.success.totalPages;
        page++;
      }

      const escape = (value) => {
        const text = value === null || value === undefined ? "" : String(value);
        return /[",\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
      };
      const header = columns.map((column) => escape(column.text)).join(",");
      const lines = rows.map((row) =>
        columns
          .map((column) => {
            const cell = row[column.dataField];
            return escape(column.formatter ? column.formatter(cell, row) : cell);
          })
          .join(",")
      );
      // BOM so Excel reads accents correctly
      const blob = new Blob(["\uFEFF" + [header, ...lines].join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "prix_achat.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setOpenErrorAlert(true);
      setErrorAlertText("Erreur : " + (e.message || e));
    } finally {
      setExporting(false);
    }
  };

  const handleErrorClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenErrorAlert(false);
  };

  const priceFormatter = (cell) =>
    cell !== null && cell !== undefined ? Number(cell).toFixed(3) : "";

  const quantityFormatter = (cell) =>
    cell !== null && cell !== undefined ? Number(cell) : "";

  const dateFormatter = (cell) => {
    if (!cell) return "";
    return new Date(cell).toLocaleDateString("fr-FR");
  };

  const columns = [
    { dataField: "productCode", text: "Code article" },
    { dataField: "brandName", text: "Marque" },
    { dataField: "categoryName", text: "Catégorie" },
    { dataField: "familyName", text: "Famille" },
    { dataField: "subFamilyName", text: "Sous-famille" },
    { dataField: "warehouseName", text: "Dépôt" },
    {
      dataField: "buyingPrice",
      text: "Prix d'achat",
      formatter: priceFormatter,
    },
    {
      dataField: "totalQuantity",
      text: "Quantité totale",
      formatter: quantityFormatter,
    },
    {
      dataField: "lastCreationDate",
      text: "Dernier achat",
      formatter: dateFormatter,
    },
  ];

  const pageButtonRenderer = ({
    page,
    active,
    disable,
    title,
    onPageChange,
  }) => {
    const handleClick = (e) => {
      e.preventDefault();
      onPageChange(page);
    };

    if (page === "...") {
      return (
        <li className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    const activeStyle = active
      ? { backgroundColor: "black", color: "white" }
      : { backgroundColor: "white", color: "black" };

    return (
      <li className="page-item">
        <button
          className="btn btn-dark"
          onClick={handleClick}
          style={activeStyle}
          disabled={disable}
          title={title}
        >
          {page}
        </button>
      </li>
    );
  };

  const options = {
    page: currentPage,
    sizePerPage: sizePerPage,
    totalSize: totalSize,
    pageButtonRenderer,
    showTotal: true,
    paginationSize: 5,
    withFirstAndLast: true,
    alwaysShowAllBtns: false,
    hideSizePerPage: false,
    sizePerPageList: [
      { text: "20", value: 20 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
    ],
  };

  const handleTableChange = (type, { page, sizePerPage: newSize }) => {
    if (type === "pagination") {
      // changing the page size goes back to the first page
      const nextPage = newSize !== sizePerPage ? 1 : page;
      setCurrentPage(nextPage);
      setSizePerPage(newSize);
      fetchPurchases(nextPage, newSize, appliedFilters);
    }
  };

  if (!userRole.includes("ADMIN")) {
    return (
      <div className="content-wrapper" style={{ marginTop: "0" }}>
        <section className="content-header">
          <div className="container-fluid">
            <p>Accès réservé aux administrateurs.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: "black" }}>
                    Home
                  </Link>
                </li>
                <li
                  className="breadcrumb-item active"
                  style={{ color: "black" }}
                >
                  Prix d'achat
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-12">
              <div className="card card-dark">
                <div className="card-body">
                  <div className="row">
                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Code article</label>
                        <TextField
                          style={{ width: "100%" }}
                          variant="outlined"
                          size="small"
                          value={filters.productCode}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              productCode: e.target.value,
                            })
                          }
                          onKeyDown={(e) =>
                            e.key === "Enter" && onApplyFiltersHandler()
                          }
                        />
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Marque</label>
                        <TextField
                          style={{ width: "100%" }}
                          variant="outlined"
                          size="small"
                          value={filters.brandName}
                          onChange={(e) =>
                            setFilters({ ...filters, brandName: e.target.value })
                          }
                          onKeyDown={(e) =>
                            e.key === "Enter" && onApplyFiltersHandler()
                          }
                        />
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Dépôt</label>
                        <Select
                          value={selectedWarehouse}
                          onChange={onChangeWarehouse}
                          options={allWarehouses}
                        />
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Date début</label>
                        <TextField
                          style={{ width: "100%" }}
                          variant="outlined"
                          type="date"
                          size="small"
                          value={filters.dateFrom.slice(0, 10)}
                          onChange={onChangeDateFrom}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Date fin</label>
                        <TextField
                          style={{ width: "100%" }}
                          variant="outlined"
                          type="date"
                          size="small"
                          value={filters.dateTo.slice(0, 10)}
                          onChange={onChangeDateTo}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer">
                  <div className="col-sm-12 text-center">
                    <button
                      className="btn btn-dark mx-1"
                      onClick={onApplyFiltersHandler}
                    >
                      Valider le choix
                    </button>
                    <button
                      className="btn btn-outline-dark mx-1"
                      onClick={onResetFiltersHandler}
                    >
                      Réinitialiser
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    Prix d'achat des produits ({totalSize})
                  </h3>
                  <button
                    className="btn btn-dark float-right ml-2"
                    onClick={() => navigate("/ajouterAchats")}
                  >
                    <i className="fas fa-plus"></i> Ajouter des achats
                  </button>
                  <button
                    className="btn btn-dark float-right"
                    disabled={exporting || totalSize === 0}
                    onClick={onExportCSVHandler}
                  >
                    <i className="fas fa-download"></i>{" "}
                    {exporting ? "Génération..." : "Télécharger CSV"}
                  </button>
                </div>
                <div className="card-body">
                  <BootstrapTable
                    keyField="rowKey"
                    data={purchases}
                    columns={columns}
                    remote={{ pagination: true, sort: false, filter: false }}
                    onTableChange={handleTableChange}
                    pagination={paginationFactory(options)}
                    striped
                    hover
                    condensed
                    noDataIndication={() => (
                      <div className="text-center py-4">
                        {isLoading ? (
                          <div>
                            <i className="fas fa-spinner fa-spin fa-2x text-dark mb-2"></i>
                            <p>Chargement en cours...</p>
                          </div>
                        ) : (
                          <div>
                            <i className="fas fa-box-open fa-2x text-muted mb-2"></i>
                            <p>Aucun achat trouvé</p>
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Snackbar
        open={openErrorAlert}
        autoHideDuration={3000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert variant="filled" onClose={handleErrorClose} severity="error">
          {errorAlertText}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default withLoader(PrixAchat);
